import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '@/lib/store'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import { supabase } from '@/lib/supabase'
import { useLanguage } from '@/lib/context/LanguageContext'
import { useTranslation } from 'react-i18next'
import {
  User,
  Mail,
  Phone,
  Lock,
  Shield,
  Bell,
  Globe,
  Loader2,
  CheckCircle,
  AlertCircle,
  MapPin,
  Languages,
  ArrowLeft,
  Eye,
  EyeOff,
  Check,
  X
} from 'lucide-react'

export function ResetPasswordPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [resetComplete, setResetComplete] = useState(false)
  
  // Password strength criteria
  const [passwordCriteria, setPasswordCriteria] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false
  })

  useEffect(() => {
    // Check password strength
    setPasswordCriteria({
      minLength: password.length >= 6,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[^A-Za-z0-9]/.test(password)
    })
  }, [password])

  // Calculate password strength
  const getPasswordStrength = () => {
    const criteria = Object.values(passwordCriteria);
    const trueCount = criteria.filter(Boolean).length;
    
    if (trueCount <= 1) return { strength: 'weak', color: 'bg-red-500', width: '20%' };
    if (trueCount <= 3) return { strength: 'medium', color: 'bg-yellow-500', width: '60%' };
    return { strength: 'strong', color: 'bg-green-500', width: '100%' };
  }

  const passwordStrength = getPasswordStrength();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate passwords
    if (password !== confirmPassword) {
      setToastMessage('Les mots de passe ne correspondent pas')
      setToastType('error')
      setShowToast(true)
      return
    }
    
    if (password.length < 6) {
      setToastMessage(t('settings.security.passwordRequirements'))
      setToastType('error')
      setShowToast(true)
      return
    }
    
    setLoading(true)

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      })

      if (error) throw error

      setResetComplete(true)
      setToastMessage('Votre mot de passe a été réinitialisé avec succès')
      setToastType('success')
      setShowToast(true)

      // Redirect after a delay
      setTimeout(() => {
        navigate('/login')
      }, 3000)
    } catch (error) {
      console.error('Error resetting password:', error)
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors de la réinitialisation du mot de passe'
      setToastMessage(errorMessage)
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  if (resetComplete) {
    return (
      <div 
        className="fixed inset-0 flex flex-col justify-center items-center bg-cover bg-center"
        style={{ 
          backgroundImage: "url('https://images.unsplash.com/photo-1579546929518-9e396f3cc809?auto=format&fit=crop&q=80')",
          backgroundColor: "#0056b3",
          backgroundBlendMode: "overlay",
          backgroundSize: "cover"
        }}
      >
        <div className="bg-white/90 backdrop-blur-sm py-8 px-4 shadow-xl shadow-primary/20 sm:rounded-xl sm:px-10 border border-white/20 max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="mt-4 text-2xl font-bold text-gray-900">Mot de passe réinitialisé</h2>
            <p className="mt-2 text-gray-600">
              Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
            </p>
            <Link to="/login">
              <Button variant="primary" className="mt-6 w-full">
                Se connecter
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="fixed inset-0 flex flex-col justify-center items-center bg-cover bg-center"
      style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1579546929518-9e396f3cc809?auto=format&fit=crop&q=80')",
        backgroundColor: "#0056b3",
        backgroundBlendMode: "overlay",
        backgroundSize: "cover"
      }}
    >
      <div className="">
        {showToast && (
          <Toast
            type={toastType}
            title={toastMessage}
            onClose={() => setShowToast(false)}
          />
        )}
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md pointer-events-auto">
        <h2 className="text-center text-2xl sm:text-3xl font-extrabold text-white">
          Réinitialiser votre mot de passe
        </h2>
        <p className="mt-2 text-center text-sm text-white/80">
          <Link to="/login" className="font-medium text-white hover:text-white/80 flex items-center justify-center">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Retour à la connexion
          </Link>
        </p>
      </div>

      <div className="mt-6 sm:mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white/90 backdrop-blur-sm py-6 sm:py-8 px-4 shadow-xl shadow-primary/20 sm:rounded-xl sm:px-10 border border-white/20">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Nouveau mot de passe
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-10 py-2 sm:py-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="Nouveau mot de passe"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
              
              {/* Password strength indicator */}
              {password.length > 0 && (
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div 
                      className={`h-1.5 rounded-full ${passwordStrength.color}`} 
                      style={{ width: passwordStrength.width }}
                    ></div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    <p className="mb-1">Force du mot de passe: <span className="font-medium">{
                      passwordStrength.strength === 'weak' ? 'Faible' :
                      passwordStrength.strength === 'medium' ? 'Moyen' : 'Fort'
                    }</span></p>
                    <ul className="space-y-1 mt-2">
                      <li className="flex items-center">
                        {passwordCriteria.minLength ? 
                          <Check className="h-3 w-3 text-green-500 mr-1" /> : 
                          <X className="h-3 w-3 text-red-500 mr-1" />}
                        <span>Au moins 6 caractères</span>
                      </li>
                      <li className="flex items-center">
                        {passwordCriteria.hasUppercase ? 
                          <Check className="h-3 w-3 text-green-500 mr-1" /> : 
                          <X className="h-3 w-3 text-red-500 mr-1" />}
                        <span>Au moins une lettre majuscule (A-Z)</span>
                      </li>
                      <li className="flex items-center">
                        {passwordCriteria.hasLowercase ? 
                          <Check className="h-3 w-3 text-green-500 mr-1" /> : 
                          <X className="h-3 w-3 text-red-500 mr-1" />}
                        <span>Au moins une lettre minuscule (a-z)</span>
                      </li>
                      <li className="flex items-center">
                        {passwordCriteria.hasNumber ? 
                          <Check className="h-3 w-3 text-green-500 mr-1" /> : 
                          <X className="h-3 w-3 text-red-500 mr-1" />}
                        <span>Au moins un chiffre (0-9)</span>
                      </li>
                      <li className="flex items-center">
                        {passwordCriteria.hasSpecial ? 
                          <Check className="h-3 w-3 text-green-500 mr-1" /> : 
                          <X className="h-3 w-3 text-red-500 mr-1" />}
                        <span>Au moins un caractère spécial (!@#$%^&*)</span>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirmer le mot de passe
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="block w-full pl-10 pr-10 py-2 sm:py-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="Confirmer le mot de passe"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
              {password && confirmPassword && password !== confirmPassword && (
                <p className="mt-1 text-xs text-red-500">Les mots de passe ne correspondent pas</p>
              )}
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                variant="primary"
                disabled={loading || password !== confirmPassword}
                className="w-full sm:w-auto"
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : null}
                Réinitialiser le mot de passe
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}